import React from 'react'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
// @src imports
import { Button, Icon } from 'src/chrome'
import { getPaperTypes } from 'src/app/constants'
import { OrderTypes } from 'src/orders/orderType'
import * as SendDelayOptions from 'src/orders/sendDelayOptions'
import { CardPanelView } from 'src/orders/components'
import { baseCost, upgradeCost } from 'src/helpers/payments'
import { isCardCostFree } from 'src/helpers'
import {
  CardPaperType,
  CardSendType,
  CardType,
  LabeledPrice,
} from 'src/graphql'
import { OrderWithPartialLines } from 'src/redux/reducers/orders'
import { PartialLine } from 'src/helpers/multitouch'
import { FlexColumn, FlexRow, OrderOverviewTitle } from 'src/styled'
import { useAccount, useSelector, useState } from 'src/hooks'

import styles from 'src/chrome/styles/detailView.module.scss'
import lineStyles from './singleCard.module.scss'
import { Div, Spacer, Text } from '@sendoutcards/quantum-design-ui'

type SingleCardProps = {
  cardImageLoading: boolean
  activeLine: PartialLine
  order: OrderWithPartialLines
  onAddGift: () => void
  onClose: () => void
  onDeselectGift: () => void
  onToggleSendDelay: () => void
  onUpdatePaperType: (type: CardPaperType) => void
  onReplaceCard: (cardId: string) => void
  onEditCard: () => void
}

const SingleCardDetail: React.FC<SingleCardProps> = props => {
  const {
    order,
    onClose,
    onUpdatePaperType,
    onEditCard,
    onReplaceCard,
    onToggleSendDelay,
    onDeselectGift,
    onAddGift,
  } = props

  const { orderType } = useSelector(state => state.orders)

  const { isQualifiedForFirstFreeCard } = useAccount()

  const [paperType, setPaperType] = useState(
    props.activeLine.card ? props.activeLine.card.paperType : null,
  )

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const paperType = event.target.value as CardPaperType
    setPaperType(paperType)
    onUpdatePaperType(paperType)
  }

  const activeLine =
    orderType === OrderTypes.multiLine
      ? props.activeLine
      : order.lines.length && order.lines[0]

  if (!activeLine) {
    return null
  }

  const isSent = order.isSent
  const costEntries = (activeLine.cost ?? { entries: Array<LabeledPrice>() })
    .entries
  const baseCostEntries = (
    activeLine.baseCost ?? { entries: Array<LabeledPrice>() }
  ).entries

  const { card } = activeLine

  // We only need this until point prices go up.
  const displayPrice = baseCostEntries && baseCost(baseCostEntries)

  // Convert type of STD into long-form "Standard", as per copy
  const paperTypeDisplayString =
    activeLine.card?.paperType === 'STD'
      ? 'Standard'
      : activeLine.card?.paperType

  return (
    <section className={`${styles.previewSection} ${styles.overviewCard}`}>
      {orderType === OrderTypes.multiLine && (
        <span
          style={{
            position: 'absolute',
            top: 5,
            right: 5,
          }}
          onClick={() => onClose()}
        >
          <Icon icon={'CLOSE'} color={'#6f8394'} size={18} />
        </span>
      )}
      <FlexRow>
        <div style={{ width: '50%' }}>
          {card && <CardPanelView card={card} />}
          {!isSent && (
            <FlexRow style={{ justifyContent: 'center', marginTop: '1rem' }}>
              <Button
                title={'Edit'}
                buttonColor={'pink'}
                onClick={() => onEditCard()}
                style={{ marginRight: '15px' }}
              />
              {card && (
                <Button
                  title={'Replace'}
                  buttonColor={'blue'}
                  onClick={() => onReplaceCard(card.id)}
                />
              )}
            </FlexRow>
          )}
        </div>
        <div className={styles.cardInfo}>
          <OrderOverviewTitle>
            <Text type="largeBody" color="primaryHeading">
              {activeLine.card && activeLine.card.detailedSendableCard
                ? activeLine.card.detailedSendableCard.title
                : 'Custom Card'}
            </Text>
          </OrderOverviewTitle>
          <div className={styles.cardActions}>
            <Text type="caption">
              {activeLine.card?.detailedSendableCard?.description ?? ''}
            </Text>
            <Spacer space="x2" />
            <FlexColumn className={lineStyles.details}>
              <FlexColumn>
                <div className={lineStyles.itemDetail}>
                  <Icon icon={'TWOPANEL'} />
                  <Spacer space="x1" orientation="horizontal" />
                  <Text type="body" weight="bold">
                    Type:
                  </Text>
                  <Spacer space="x1" orientation="horizontal" />
                  <Text type="body">{activeLine.card?.type}</Text>
                </div>
                <div className={lineStyles.itemDetail}>
                  <Icon icon={'FLATCARD'} color={'#6f8394'} />
                  <Spacer space="x1" orientation="horizontal" />
                  <Text type="body" weight="bold">
                    Paper:
                  </Text>
                  <Spacer space="x1" orientation="horizontal" />
                  {isQualifiedForFirstFreeCard ? (
                    <Text type="body">{paperTypeDisplayString}</Text>
                  ) : (
                    <TextField
                      select={true}
                      value={paperType}
                      style={{ width: 'auto', minWidth: 155 }}
                      onChange={handleSelect}
                    >
                      {getPaperTypes(
                        activeLine.card
                          ? activeLine.card.type
                          : CardType.TWO_PANEL,
                      ).map((type, index) => (
                        <MenuItem key={index} value={type.value}>
                          {type.upgrade
                            ? `${type.label} + ${type.upgrade}`
                            : type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </div>
                <div className={lineStyles.itemDetail} style={{ flex: '2' }}>
                  <Icon
                    icon={'CALENDAR'}
                    color={'#6f8394'}
                    viewBox={'0 0 32 32'}
                  />
                  <Spacer space="x1" orientation="horizontal" />
                  <Text type="body" weight="bold">
                    Schedule:
                  </Text>
                  <Spacer space="x1" orientation="horizontal" />
                  <Button
                    id={'sendDelay'}
                    title={
                      activeLine.sendDelay &&
                      SendDelayOptions.verboseDescription(
                        activeLine.sendDelay,
                        'MMM Do, YYYY',
                      )
                    }
                    buttonColor={'blue'}
                    onClick={() => onToggleSendDelay()}
                    titleStyle={{ fontSize: '1em' }}
                  />

                  <div className={lineStyles.sendDelayInfo}>
                    <Icon icon={'INFO'} size={15} color={'#6f8394'} />
                    <span className={lineStyles.sendDelayTooltip}>
                      <Text type="caption">
                        Cards with an anniversary or birthday delay are
                        scheduled to print and send 7 days before the scheduled
                        date.
                      </Text>
                    </span>
                  </div>
                </div>
              </FlexColumn>
              <div className={lineStyles.itemDetail}>
                <Icon
                  icon={'CONFETTI'}
                  color={'#6f8394'}
                  viewBox={'0 0 32 32'}
                />
                <Spacer space="x1" orientation="horizontal" />
                <Text type="body" weight="bold">
                  Send Type:
                </Text>
                <Spacer space="x1" orientation="horizontal" />
                <Text type="body">
                  {order.sendType === CardSendType.HEARTFELT
                    ? 'Heartfelt'
                    : 'Automated Send'}
                </Text>
              </div>
              <div className={lineStyles.itemDetail}>
                <Icon
                  icon={'MONEY'}
                  size={34}
                  color={'#6f8394'}
                  viewBox={'0 0 32 32'}
                />
                <Spacer space="x1" orientation="horizontal" />
                <Text type="body" weight="bold">
                  Price:
                </Text>
                <Spacer space="x1" orientation="horizontal" />
                <Div display="flex">
                  <Text
                    type="body"
                    isStrikeThrough={isCardCostFree(costEntries)}
                  >
                    {displayPrice}
                  </Text>
                  {isCardCostFree(costEntries) && (
                    <>
                      <Spacer space="x1" orientation="horizontal" />
                      <Text type="body">$0.00</Text>
                    </>
                  )}
                  {!!upgradeCost(baseCostEntries) && (
                    <>
                      <Spacer space="x1" orientation="horizontal" />
                      <Text type="caption">
                        + {upgradeCost(baseCostEntries)} (card upgrades)
                      </Text>
                    </>
                  )}
                </Div>
              </div>
            </FlexColumn>
          </div>
          {activeLine.card && activeLine.card.type !== 'POSTCARD' && (
            <div className={lineStyles.giftContainer}>
              <OrderOverviewTitle>
                <Text type="largeBody" color="primaryHeading">
                  Gift
                </Text>
              </OrderOverviewTitle>
              {activeLine.giftVariation ? (
                <div>
                  <div className={lineStyles.gift}>
                    <img
                      className={lineStyles.giftImage}
                      src={activeLine.giftVariation.defaultImageUrl}
                      alt="Gift"
                    />
                    <FlexColumn>
                      <div className={lineStyles.giftDetails}>
                        <Text type="body" weight="bold">
                          Title:
                        </Text>
                        <Spacer space="x1" orientation="horizontal" />
                        <Text type="body">{activeLine.giftVariation.name}</Text>
                      </div>
                      <div className={lineStyles.giftDetails}>
                        <Text type="body" weight="bold">
                          Price:
                        </Text>
                        <Spacer space="x1" orientation="horizontal" />
                        <Text type="body">
                          {activeLine.giftVariation?.price.asString ?? ''}
                        </Text>
                      </div>
                    </FlexColumn>
                    <button
                      id={'deselectGift'}
                      className={lineStyles.closeButton}
                      onClick={() => onDeselectGift()}
                    >
                      <Icon icon={'CLOSE'} color={'#F262B1'} size={13} />
                    </button>
                  </div>
                  <FlexRow
                    style={{ justifyContent: 'center', marginTop: '1rem' }}
                  >
                    <Button
                      id={'deselect_gift_btn'}
                      title={'Remove Gift'}
                      buttonColor={'pink'}
                      style={{ margin: 'auto', marginTop: 10 }}
                      onClick={() => onDeselectGift()}
                    />
                    <Button
                      id={'change_gift_btn'}
                      title={'Change Gift'}
                      buttonColor={'pink'}
                      style={{ margin: 'auto', marginTop: 10 }}
                      onClick={() => onAddGift()}
                    />
                  </FlexRow>
                </div>
              ) : (
                <div style={{ paddingTop: 45 }}>
                  <div className={lineStyles.flex}>
                    <Icon
                      icon={'GIFT'}
                      size={34}
                      color={'#6f8394'}
                      viewBox={'0 0 46 32'}
                    />
                    <Spacer space="x1" orientation="horizontal" />
                    <Text type="caption">No gift selected</Text>
                  </div>
                  {!isSent && (
                    <Button
                      id={'add_a_gift_btn'}
                      title={'Add Gift'}
                      buttonColor={'pink'}
                      style={{
                        marginTop: '25px',
                      }}
                      onClick={() => onAddGift()}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </FlexRow>
    </section>
  )
}

export default SingleCardDetail
